import { defineStore } from 'pinia';
import { ref } from 'vue';
import {
  getAcknowledgementsConfig,
  normalizeAcknowledgementsConfig,
  normalizeAcknowledgementsConfigDefaults,
  updateAcknowledgementsConfig,
} from '@/api/settings';
import { handleKnownSettingsApiErrors } from '@/stores/settings/errorHandling';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { useAppLoading } from '@/stores/app/loading';
import { useAcknowledgementSettingsForm } from '@/stores/settings/acknowledgementSettingsForm';

export const useAcknowledgementSettings = defineStore('acknowledgementSettings', () => {
  const form = useAcknowledgementSettingsForm();
  const { setLoading } = useAppLoading();
  const { addErrorSnackbar, addSuccessSnackbar } = useAppSnackbar();

  const defaults = ref({});

  const handleValidationError = (error) => {
    const { response } = error;

    if (response && response.code === 'validation_failed') {
      const attrs = response.attributes || {};
      form.setFormValues(normalizeAcknowledgementsConfig(attrs));
      defaults.value = normalizeAcknowledgementsConfigDefaults(attrs);
      form.setBackendErrors(response.errors);
      return true;
    }

    return false;
  };

  const handleError = (error) => {
    const isKnownError = handleKnownSettingsApiErrors({ context: 'Acknowledgements settings', error });

    if (!isKnownError) {
      addErrorSnackbar({ text: `Acknowledgements settings - ${error.message}` });
      throw error;
    }
  };

  const load = async () => {
    try {
      setLoading({ pageLoading: true });
      const data = await getAcknowledgementsConfig();
      form.setFormValues(data.attributes);
      defaults.value = data.defaults;
    } catch (error) {
      handleError(error);
    } finally {
      setLoading({ pageLoading: false });
    }
  };

  const update = async (sectionKeys = null) => {
    const isValid = sectionKeys
      ? sectionKeys.every((name) => !form.errorMessages[name])
      : form.isFormValid;

    if (!isValid) {
      return false;
    }

    try {
      setLoading({ componentLoading: true });

      const data = await updateAcknowledgementsConfig(
        form.values,
        sectionKeys ?? undefined,
      );

      if (data.success && data.attributes) {
        const sectionData = sectionKeys
          ? Object.fromEntries(sectionKeys.map((key) => [key, data.attributes[key]]))
          : data.attributes;

        form.setFormValues(sectionData);
        defaults.value = data.defaults;
        addSuccessSnackbar({
          text: 'Acknowledgements settings have been updated',
        });
        return true;
      }
    } catch (error) {
      if (!handleValidationError(error)) {
        handleError(error);
      }
    } finally {
      setLoading({ componentLoading: false });
    }

    return false;
  };

  return { load, update, defaults };
});
